import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Download, FileText, Users, Euro } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  active: "bg-green-600/15 border-green-600/30 text-green-400",
  trial: "bg-[#3A5CC8]/15 border-blue-600/30 text-blue-400",
  expired: "bg-red-600/15 border-red-600/30 text-red-400",
};
const STATUS_LABELS = { active: "Abonné", trial: "Essai", expired: "Essai terminé" };

const beDate = (value) => (value ? value.slice(0, 10).split("-").reverse().join("/") : "—");
const money = (value) => `${Number(value).toFixed(2).replace(".", ",")} €`;

export const AdminSubscriptions = ({ token }) => {
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [month, setMonth] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    axios.get(`${API}/admin/subscriptions`, { headers }).then(({ data }) => setData(data)).catch(() => {});
    axios
      .get(`${API}/admin/invoices${month ? `?month=${month}` : ""}`, { headers })
      .then(({ data }) => setInvoices(data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, token]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/invoices/export${month ? `?month=${month}` : ""}`, {
        headers, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `devisto-factures-${month || "toutes"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export impossible.");
    }
  };

  return (
    <div data-testid="admin-subscriptions" className="mt-8 space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[
          { label: "Abonnés payants", value: data?.active ?? "—", icon: Users, testId: "stat-active" },
          { label: "En essai", value: data?.trial ?? "—", icon: Users, testId: "stat-trial" },
          { label: "Revenu mensuel HTVA", value: data ? money(data.mrr_excl) : "—", icon: Euro, testId: "stat-mrr" },
          { label: "Factures affichées", value: invoices?.invoices?.length ?? "—", icon: FileText, testId: "stat-invoices" },
        ].map((s) => (
          <div key={s.label} data-testid={`admin-sub-${s.testId}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5">
            <s.icon className="w-4 h-4 text-blue-400" />
            <p className="mt-3 font-heading text-2xl font-bold">{s.value}</p>
            <p className="mt-1 text-xs text-[#5C6A87]">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-heading text-xl font-semibold">Export comptable</h2>
          <select
            data-testid="admin-invoices-month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-xl border border-slate-700 bg-slate-950 px-4 py-2 text-sm text-slate-200"
          >
            <option value="">Toutes les périodes</option>
            {(invoices?.months || []).map((m) => (
              <option key={m} value={m}>{m.split("-").reverse().join("/")}</option>
            ))}
          </select>
          <button
            type="button"
            data-testid="admin-invoices-export"
            onClick={exportCsv}
            className="inline-flex items-center gap-2 rounded-full bg-[#3A5CC8] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#3A5CC8] transition-colors duration-200"
          >
            <Download className="w-4 h-4" /> Télécharger le CSV
          </button>
        </div>
        <p className="mt-3 text-xs text-[#5C6A87]">
          Une ligne par facture : numéro, date, client, n° de TVA, base HTVA, TVA 21 %, total TVAC et référence Stripe.
          Fichier prêt à envoyer à votre comptable (séparateur point-virgule, ouvrable dans Excel).
        </p>
        {!!invoices && (
          <p className="mt-4 text-sm text-slate-300">
            {invoices.invoices.length} facture(s) — {money(invoices.total_excl)} HTVA · TVA {money(invoices.total_vat)} ·
            <strong className="ml-1 text-white">{money(invoices.total)} TVAC</strong>
          </p>
        )}
        <div className="mt-5 space-y-2">
          {(invoices?.invoices || []).slice(0, 12).map((inv) => (
            <div key={inv.id} data-testid={`admin-invoice-${inv.number}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 px-4 py-3">
              <span className="text-sm font-bold text-white">{inv.number}</span>
              <span className="text-xs text-[#5C6A87]">{beDate(inv.issued_at)}</span>
              <span className="text-sm text-slate-300">{inv.buyer?.company || inv.owner_email}</span>
              <span className="ml-auto text-sm font-semibold text-white">{money(inv.total)}</span>
            </div>
          ))}
          {invoices && !invoices.invoices.length && (
            <p className="text-sm text-[#5C6A87]">Aucune facture pour cette période.</p>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
        <h2 className="font-heading text-xl font-semibold">Sociétés inscrites ({data?.total ?? 0})</h2>
        <div className="mt-5 space-y-2">
          {(data?.subscriptions || []).map((s) => (
            <div key={s.owner_email} data-testid={`admin-sub-${s.owner_email}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-800 px-4 py-3">
              <span className={`rounded-full border px-3 py-1 text-xs font-bold ${STATUS_STYLES[s.status]}`}>
                {STATUS_LABELS[s.status]}
              </span>
              <span className="text-sm font-semibold text-white">{s.company || s.owner_email}</span>
              <span className="text-xs text-[#5C6A87]">{s.owner_email}</span>
              {!!s.vat && <span className="text-xs text-[#5C6A87]">TVA {s.vat}</span>}
              <span className="text-xs text-[#5C6A87]">{s.members} ouvrier(s)</span>
              <span className="ml-auto text-xs text-[#5C6A87]">
                {s.status === "active"
                  ? `Renouvellement ${beDate(s.current_period_end)}`
                  : `Essai jusqu'au ${beDate(s.trial_ends_at)}`}
              </span>
            </div>
          ))}
          {data && !data.subscriptions.length && <p className="text-sm text-[#5C6A87]">Aucune société pour le moment.</p>}
        </div>
      </div>
    </div>
  );
};
